import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Loading from '../../../components/Loading';
import usePermissions from '../../../components/Menu/MenuItems/hooks/usePermissions';
import BankAccountStatusBadge from '../components/BankAccountStatusBadge';
import IssuanceBadge from '../components/IssuanceBadge';
import { formatDocForDisplay } from '../../../utils/format';
import { formatBankLine, orDash } from '../utils/format';
import { financeErrorMessage } from '../utils/apiError';
import { listBankAccounts } from '../../../services/finance/bankAccountsApi';
import { EmptyState } from '../../../styles/feedback/EmptyState';
import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../styles/layout/Section';
import {
   ActionBar,
   ActionBarLeft,
   ActionBarRight,
} from '../../../styles/layout/ActionBar';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';
import { TableWrapper } from '../../../styles/table/TableWrapper';
import { Table } from '../../../styles/table/Table';
import { ClickableRow, TextLink } from '../../../styles/ui/Link';
import { CtaButton } from '../../../styles/ui/Button';

// ---------------------------------------------------------------------------
// Financeiro · Contas bancárias — listagem (handoff 0079 §3.1).
//
// CATÁLOGO, não tela de pesquisa: o volume é de unidades (um CNPJ por conta),
// então a lista CARREGA NO MOUNT mesmo com a URL limpa. É um desvio consciente
// do navegacao.md §1.4 (decisão do Caio, 2026-08-04), com o mesmo precedente do
// OriginsCatalog e da Triagem de Vendas — abrir vazio seria só atrito.
//
// O resto do capítulo 1 vale: os filtros (search/status) são URL-driven, a busca
// é explícita (submit), e a URL é gravada com replace:true para que o "Voltar"
// da ficha restaure exatamente esta lista sem empilhar histórico.
//
// Não há paginação porque o backend não pagina neste recurso.
// Não há ordenação clicável porque o backend não declara whitelist de order_by
// (navegacao.md §1.10: listagem sem ordenação > ordenação fantasma).
// ---------------------------------------------------------------------------

const DEFAULT_FILTERS = {
   search: '',
   status: '',
};

function readFiltersFromUrl(searchParams) {
   const next = { ...DEFAULT_FILTERS };
   Object.keys(DEFAULT_FILTERS).forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null && value !== '') next[key] = value;
   });
   return next;
}

export default function BankAccountList() {
   const navigate = useNavigate();
   const { hasPermission } = usePermissions();
   const canCreate = hasPermission('finance:bank_account:create');

   const [searchParams, setSearchParams] = useSearchParams();

   // `filters` é o rascunho (ligado aos inputs); `appliedFilters` é o último
   // efetivamente pesquisado — é ele que vai para a API. Leitura da URL só no
   // mount (URL = fonte da verdade na entrada, não durante a digitação).
   const [filters, setFilters] = useState(() => readFiltersFromUrl(searchParams));
   const [appliedFilters, setAppliedFilters] = useState(filters);

   const [accounts, setAccounts] = useState([]);
   const [isLoading, setIsLoading] = useState(true);
   const didInitialFetch = useRef(false);

   const fetchAccounts = useCallback(async (values) => {
      setIsLoading(true);
      try {
         const list = await listBankAccounts(values);
         setAccounts(list);
      } catch (err) {
         toast.error(
            financeErrorMessage(err, 'Erro ao carregar as contas bancárias.'),
         );
         setAccounts([]);
      } finally {
         setIsLoading(false);
      }
   }, []);

   // Carga inicial (catálogo): busca sempre, com o que veio da URL.
   useEffect(() => {
      if (didInitialFetch.current) return;
      didInitialFetch.current = true;
      fetchAccounts(appliedFilters);
   }, [fetchAccounts, appliedFilters]);

   // Grava só os filtros não-vazios; replace:true para não empilhar histórico
   // (navegacao.md §1.6 — o ◀ do navegador sai da listagem, não pagina nela).
   const commitToUrl = useCallback(
      (values) => {
         const params = {};
         Object.keys(DEFAULT_FILTERS).forEach((key) => {
            if (values[key]) params[key] = values[key];
         });
         setSearchParams(params, { replace: true });
      },
      [setSearchParams],
   );

   const updateField = (key, value) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
   };

   const handleSearch = (event) => {
      event.preventDefault();
      setAppliedFilters(filters);
      commitToUrl(filters);
      fetchAccounts(filters);
   };

   const handleClear = () => {
      setFilters(DEFAULT_FILTERS);
      setAppliedFilters(DEFAULT_FILTERS);
      setSearchParams({}, { replace: true });
      // Diferente de uma tela de pesquisa: aqui limpar volta ao catálogo
      // completo, então recarrega (a lista nunca fica vazia por omissão).
      fetchAccounts(DEFAULT_FILTERS);
   };

   const hasResults = accounts.length > 0;
   const isFiltered = Boolean(appliedFilters.search || appliedFilters.status);

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Contas bancárias</PageTitle>
               <PageSubtitle>
                  Contas recebedoras e suas credenciais de integração
               </PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               {canCreate && (
                  <CtaButton
                     type="button"
                     onClick={() => navigate('/finance/bank-accounts/new')}
                  >
                     + Nova conta
                  </CtaButton>
               )}
            </PageHeaderActions>
         </PageHeader>

         {/* ─── Filtros ───────────────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Filtros</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <form onSubmit={handleSearch}>
                  <FormGrid>
                     <Field className="col-8">
                        <label htmlFor="filter-search">Busca</label>
                        <input
                           id="filter-search"
                           type="text"
                           placeholder="Apelido, titular ou documento..."
                           value={filters.search}
                           onChange={(e) =>
                              updateField('search', e.target.value)
                           }
                        />
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="filter-status">Situação</label>
                        <select
                           id="filter-status"
                           value={filters.status}
                           onChange={(e) =>
                              updateField('status', e.target.value)
                           }
                        >
                           <option value="">Todas</option>
                           <option value="active">Ativas</option>
                           <option value="inactive">Inativas</option>
                        </select>
                     </Field>
                  </FormGrid>

                  <ActionBar style={{ marginTop: 16 }}>
                     <ActionBarLeft />
                     <ActionBarRight>
                        <TextLink
                           as="button"
                           type="button"
                           onClick={handleClear}
                           disabled={isLoading}
                        >
                           Limpar
                        </TextLink>
                        <CtaButton type="submit" disabled={isLoading}>
                           Pesquisar
                        </CtaButton>
                     </ActionBarRight>
                  </ActionBar>
               </form>
            </SectionBody>
         </Section>

         {/* ─── Resultados ────────────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>
                  Contas{accounts.length ? ` (${accounts.length})` : ''}
               </SectionTitle>
            </SectionHeader>
            <SectionBody>
               {hasResults ? (
                  <TableWrapper>
                     <Table>
                        <thead>
                           <tr>
                              <th>Apelido</th>
                              <th>Titular</th>
                              <th>CPF/CNPJ</th>
                              <th>Dados bancários</th>
                              {/* Aptidão para emitir vem do serializer
                                  (has_active_credential) — evita abrir a ficha
                                  só para descobrir que a conta não emite. */}
                              <th className="center">Emissão</th>
                              <th className="center">Situação</th>
                           </tr>
                        </thead>
                        <tbody>
                           {accounts.map((account) => (
                              <ClickableRow
                                 key={account.id}
                                 onClick={() =>
                                    navigate(
                                       `/finance/bank-accounts/${account.id}`,
                                    )
                                 }
                              >
                                 <td>{orDash(account.account_name)}</td>
                                 <td>{orDash(account.account_owner_name)}</td>
                                 <td>
                                    {formatDocForDisplay(
                                       account.account_doc_number,
                                    )}
                                 </td>
                                 <td>{formatBankLine(account)}</td>
                                 <td className="center">
                                    <IssuanceBadge
                                       value={account.has_active_credential}
                                    />
                                 </td>
                                 <td className="center">
                                    <BankAccountStatusBadge
                                       value={account.status}
                                    />
                                 </td>
                              </ClickableRow>
                           ))}
                        </tbody>
                     </Table>
                  </TableWrapper>
               ) : (
                  <EmptyState
                     title={
                        isFiltered
                           ? 'Nenhuma conta encontrada'
                           : 'Nenhuma conta bancária cadastrada'
                     }
                     description={
                        isFiltered
                           ? 'Ajuste os filtros e clique em Pesquisar.'
                           : 'Enquanto não houver conta cadastrada, a emissão de cobranças continua usando a configuração antiga do servidor. Cadastre a primeira conta para gerenciar as chaves por aqui.'
                     }
                  />
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
